import { Link } from '@/components/LocalizedLink';
import { useLanguage } from '@/contexts/LanguageContext';
import type { WebsiteProject } from '@/lib/contentService';
import { getLocalizedText, getProjectCoverImage } from '@/lib/contentService';
import projectFallback from '@/assets/project-1.webp';

type RelatedProjectsBlockProps = {
  projects: WebsiteProject[];
  title?: string;
};

const RelatedProjectsBlock = ({ projects, title }: RelatedProjectsBlockProps) => {
  const { lang } = useLanguage();

  if (!projects.length) return null;

  return (
    <section className="mt-16 border-t border-border pt-12">
      <h2 className="font-display text-3xl font-bold text-foreground mb-6">
        {title || (lang === 'tr' ? 'İlgili Projeler' : 'Related Projects')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {projects.map((project) => (
          <Link key={project.id} to={`/projects/${project.slug}`} className="group rounded-xl border border-border/70 overflow-hidden bg-card/40">
            <img
              src={getProjectCoverImage(project) || projectFallback}
              alt={getLocalizedText(project.name_i18n, lang, project.name)}
              className="w-full aspect-[16/10] object-cover group-hover:scale-[1.03] transition-transform duration-300"
              loading="lazy"
              decoding="async"
            />
            <div className="p-4">
              <h3 className="font-display text-lg text-foreground mb-1">
                {getLocalizedText(project.name_i18n, lang, project.name)}
              </h3>
              <p className="font-body text-sm text-muted-foreground line-clamp-2">
                {getLocalizedText(project.short_description_i18n, lang, project.short_description)}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedProjectsBlock;
